import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Alert } from '@/components/ui/Alert'
import { useTranslation } from 'react-i18next'

const linkSchema = z.object({
  invitation_code: z
    .string()
    .min(1, 'parent.link_required')
    .max(255),
})

type LinkFormData = z.infer<typeof linkSchema>

export function ParentLinkForm() {
  const { t } = useTranslation()
  const [successMsg, setSuccessMsg] = useState<string | null>(null)
  const [apiError, setApiError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<LinkFormData>({
    resolver: zodResolver(linkSchema),
  })

  const onSubmit = async (data: LinkFormData) => {
    setApiError(null)
    setSuccessMsg(null)
    try {
      await apiClient.post('/parental/link', { invitation_code: data.invitation_code })
      setSuccessMsg(t('parent.link_success'))
      reset()
    } catch {
      setApiError(t('parent.link_error'))
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
      <h2 className="text-base font-semibold text-neutral-900">{t('parent.link_title')}</h2>
      <p className="text-sm text-neutral-600">
        {t('parent.link_hint')}
      </p>

      {successMsg && <Alert variant="success">{successMsg}</Alert>}
      {apiError && <Alert variant="error">{apiError}</Alert>}

      <div className="flex gap-3 items-end">
        <div className="flex-1">
          <Input
            label={t('parent.link_label')}
            placeholder={t('parent.link_placeholder')}
            error={errors.invitation_code?.message && t(errors.invitation_code.message)}
            {...register('invitation_code')}
          />
        </div>
        <Button type="submit" isLoading={isSubmitting} className="flex-shrink-0">
          {t('parent.link_submit')}
        </Button>
      </div>
    </form>
  )
}
